import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CREDIT_GRANT_END_REASONS,
  CREDIT_GRANT_HISTORY_DEFAULT_LIMIT,
  CREDIT_GRANT_HISTORY_QUERY_MAX_CHARS,
  CREDIT_GRANT_HISTORY_STATUS_FILTERS,
  CREDIT_GRANT_TYPES,
  isCreditGrantEndReason,
  isCreditGrantHistoryStatusFilter,
  isCreditGrantType,
  isCreditOpsEventAudience,
  isCreditOpsEventType,
  type CreditGrant,
  type CreditGrantEndReason,
  type CreditGrantHistoryRow,
  type CreditGrantHistoryStatusFilter,
  type CreditGrantRepaymentStatus,
  type CreditGrantUsedMeasuredAt,
  type CreditGrantType,
  type CreditOpsEvent,
  type CreditOpsEventAudience,
  type CreditOpsEventType,
} from '@nadoutong/contracts';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useCreditGrantHistory } from '../api/credit.ts';
import { usePricing } from '../api/pricing.ts';
import { DualCurrencyAmount } from '../components/DualCurrencyAmount.tsx';
import { CustomerCell } from '../components/identity/CustomerCell.tsx';
import {
  formatDisplayRateHint,
  formatLocalTimestamp,
  formatUsdMicros,
  usdMicrosToCnyCents,
} from '../components/formatters.ts';
import { coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  Chip,
  EmptyState,
  FormField,
  IconButton,
  Input,
  KpiCard,
  PageHeader,
  Pagination,
  Panel,
  Select,
  Table,
  TableEmpty,
  TableSkeleton,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';
import type { ChipTone } from '../ui/index.ts';

/**
 * Credit grant history (router#2543): every grant the caller's book
 * can see, active and ended. Filters live in the URL so a Customer 360
 * outlink can open pre-scoped. Summary KPIs are the visible-scope full
 * set from the response — never derived from the current page.
 */

const PAGE_LIMIT = CREDIT_GRANT_HISTORY_DEFAULT_LIMIT;
const FILTER_ALL = 'all';

const GRANT_TYPE_LABEL: Record<CreditGrantType, I18nKey> = {
  long_term: 'credit.history.type.long_term',
  temporary: 'credit.history.type.temporary',
  temporary_bump: 'credit.history.type.temporary_bump',
};

const GRANT_TYPE_TONE: Record<CreditGrantType, ChipTone> = {
  long_term: 'signal',
  temporary: 'warn',
  temporary_bump: 'neutral',
};

const END_REASON_LABEL: Record<CreditGrantEndReason, I18nKey> = {
  repaid: 'credit.history.end_reason.repaid',
  expired: 'credit.history.end_reason.expired',
  manual: 'credit.history.end_reason.manual',
  superseded: 'credit.history.end_reason.superseded',
};

const END_REASON_TONE: Record<CreditGrantEndReason, ChipTone> = {
  repaid: 'ok',
  expired: 'warn',
  manual: 'neutral',
  superseded: 'signal',
};

const STATUS_LABEL: Record<CreditGrantHistoryStatusFilter, I18nKey> = {
  all: 'credit.history.status.all',
  active: 'credit.history.status.active',
  ended: 'credit.history.status.ended',
};

const EVENT_TYPE_LABEL: Record<CreditOpsEventType, I18nKey> = {
  temporary_credit_expiring: 'credit.history.event.temporary_credit_expiring',
  temporary_credit_arrival_shortfall: 'credit.history.event.temporary_credit_arrival_shortfall',
  temporary_credit_one_shot_issued: 'credit.history.event.temporary_credit_one_shot_issued',
  credit_due_soon_7d: 'credit.history.event.credit_due_soon_7d',
  credit_due_soon_3d: 'credit.history.event.credit_due_soon_3d',
  credit_due_today: 'credit.history.event.credit_due_today',
  credit_overdue_frozen: 'credit.history.event.credit_overdue_frozen',
  credit_stop_notice: 'credit.history.event.credit_stop_notice',
  credit_service_stopped: 'credit.history.event.credit_service_stopped',
  credit_collections: 'credit.history.event.credit_collections',
  credit_overdue_cleared: 'credit.history.event.credit_overdue_cleared',
  credit_exemption_opened: 'credit.history.event.credit_exemption_opened',
  credit_exemption_revoked: 'credit.history.event.credit_exemption_revoked',
  credit_overdue_reminder: 'credit.history.event.credit_overdue_reminder',
};

const AUDIENCE_LABEL: Record<CreditOpsEventAudience, I18nKey> = {
  customer: 'credit.history.audience.customer',
  internal: 'credit.history.audience.internal',
};

const AUDIENCE_TONE: Record<CreditOpsEventAudience, ChipTone> = {
  customer: 'signal',
  internal: 'neutral',
};

function parseOffsetParam(raw: string | null): number {
  if (raw === null) return 0;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

function parseStatusParam(raw: string | null): CreditGrantHistoryStatusFilter {
  if (raw !== null && isCreditGrantHistoryStatusFilter(raw)) return raw;
  return 'all';
}

function parseGrantTypeParam(raw: string | null): CreditGrantType | undefined {
  if (raw !== null && isCreditGrantType(raw)) return raw;
  return undefined;
}

function parseEndReasonParam(raw: string | null): CreditGrantEndReason | undefined {
  if (raw !== null && isCreditGrantEndReason(raw)) return raw;
  return undefined;
}

function prettyPayload(raw: string): string {
  try {
    const parsed: unknown = JSON.parse(raw);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return raw;
  }
}

function eventTypeLabelKey(eventType: string): I18nKey {
  if (isCreditOpsEventType(eventType)) return EVENT_TYPE_LABEL[eventType];
  return 'credit.history.event.unknown';
}

function audienceLabelKey(audience: string): I18nKey {
  if (isCreditOpsEventAudience(audience)) return AUDIENCE_LABEL[audience];
  return 'credit.history.audience.internal';
}

function GrantAmount({
  grant,
  cnyPerUsd,
}: {
  readonly grant: CreditGrant;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const usdMicros = Number.isSafeInteger(grant.amount_usd_micros) ? grant.amount_usd_micros : null;
  const cnyCents =
    grant.amount_cny_cents !== null && Number.isSafeInteger(grant.amount_cny_cents)
      ? grant.amount_cny_cents
      : null;
  if (cnyCents !== null) {
    const rate = formatDisplayRateHint(grant.applied_rate);
    const usd = usdMicros === null ? t('credit.overview.dash') : formatUsdMicros(usdMicros, false);
    return (
      <div
        title={t('credit.history.amount.rate_tooltip', { rate, usd })}
        data-testid={`credit-grants-amount-${grant.id}`}
      >
        <DualCurrencyAmount display="cny" cnyCents={cnyCents} signed={false} align="start" />
      </div>
    );
  }
  return (
    <DualCurrencyAmount
      display="cny"
      cnyCents={usdMicros === null ? null : usdMicrosToCnyCents(usdMicros, cnyPerUsd)}
      signed={false}
      align="start"
    />
  );
}

const USED_MEASURED_LABEL: Record<CreditGrantUsedMeasuredAt, I18nKey> = {
  now: 'credit.history.used.measured_now',
  ended_at: 'credit.history.used.measured_ended',
};

const REPAYMENT_STATUS_TONE: Record<CreditGrantRepaymentStatus, ChipTone> = {
  settled: 'ok',
  partial: 'warn',
  unpaid: 'bad',
  carried_over: 'neutral',
};

const REPAYMENT_STATUS_LABEL: Record<
  Exclude<CreditGrantRepaymentStatus, 'partial'>,
  I18nKey
> = {
  settled: 'credit.history.repayment.settled',
  unpaid: 'credit.history.repayment.unpaid',
  carried_over: 'credit.history.repayment.carried_over',
};

function repaymentPartialPct(repaid: number, outstanding: number | null): number | null {
  if (outstanding === null) return null;
  const total = repaid + outstanding;
  if (total <= 0) return null;
  return Math.round((100 * repaid) / total);
}

function oneShotStatementStatusLabel(
  status: string,
  t: (key: I18nKey, params?: { readonly [name: string]: string | number }) => string,
): string {
  if (status === 'open') return t('credit.history.repayment.statement.open');
  if (status === 'closed') return t('credit.history.repayment.statement.closed');
  return status;
}

function UsedAmount({
  grantId,
  usedUsdMicros,
  usedMeasuredAt,
  cnyPerUsd,
}: {
  readonly grantId: string;
  readonly usedUsdMicros: number;
  readonly usedMeasuredAt: CreditGrantUsedMeasuredAt;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const micros = Number.isSafeInteger(usedUsdMicros) ? usedUsdMicros : null;
  const caption = t(USED_MEASURED_LABEL[usedMeasuredAt]);
  return (
    <div data-testid={`credit-grants-used-${grantId}`} title={caption}>
      <DualCurrencyAmount
        display="cny"
        cnyCents={micros === null ? null : usdMicrosToCnyCents(micros, cnyPerUsd)}
        signed={false}
        align="start"
      />
      <span className="text-micro text-paper-low">{caption}</span>
    </div>
  );
}

function RepaymentCell({
  grantId,
  repaidUsdMicros,
  outstandingUsdMicros,
  repaymentStatus,
  oneShotStatement,
  cnyPerUsd,
}: {
  readonly grantId: string;
  readonly repaidUsdMicros: number;
  readonly outstandingUsdMicros: number | null;
  readonly repaymentStatus: CreditGrantRepaymentStatus;
  readonly oneShotStatement: CreditGrantHistoryRow['one_shot_statement'];
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const repaidMicros = Number.isSafeInteger(repaidUsdMicros) ? repaidUsdMicros : null;
  const outstandingMicros =
    outstandingUsdMicros !== null && Number.isSafeInteger(outstandingUsdMicros)
      ? outstandingUsdMicros
      : null;
  const pct = repaymentPartialPct(repaidUsdMicros, outstandingUsdMicros);
  const statusLabel =
    repaymentStatus === 'partial'
      ? t('credit.history.repayment.partial', {
          pct: pct === null ? t('credit.overview.dash') : String(pct),
        })
      : t(REPAYMENT_STATUS_LABEL[repaymentStatus]);
  return (
    <div className="flex flex-col gap-1" data-testid={`credit-grants-repayment-${grantId}`}>
      <div className="flex flex-col gap-0.5">
        <div className="flex items-baseline gap-1">
          <span>{t('credit.history.repayment.repaid')}</span>
          <DualCurrencyAmount
            display="cny"
            cnyCents={repaidMicros === null ? null : usdMicrosToCnyCents(repaidMicros, cnyPerUsd)}
            signed={false}
            align="start"
          />
        </div>
        {outstandingUsdMicros !== null ? (
          <div className="flex items-baseline gap-1">
            <span>{t('credit.history.repayment.outstanding')}</span>
            <DualCurrencyAmount
              display="cny"
              cnyCents={
                outstandingMicros === null ? null : usdMicrosToCnyCents(outstandingMicros, cnyPerUsd)
              }
              signed={false}
              align="start"
            />
          </div>
        ) : null}
      </div>
      <Chip
        tone={REPAYMENT_STATUS_TONE[repaymentStatus]}
        testId={`credit-grants-repayment-status-${grantId}`}
        data-repayment-status={repaymentStatus}
      >
        {statusLabel}
      </Chip>
      {oneShotStatement !== null ? (
        <span className="text-micro text-paper-low">
          {t('credit.history.repayment.billed', {
            status: oneShotStatementStatusLabel(oneShotStatement.status, t),
          })}
        </span>
      ) : null}
    </div>
  );
}

function EndReasonChip({
  grantId,
  reason,
}: {
  readonly grantId: string;
  readonly reason: CreditGrantEndReason;
}): JSX.Element {
  const t = useT();
  return (
    <Chip
      tone={END_REASON_TONE[reason]}
      testId={`credit-grants-end-reason-${grantId}`}
      data-end-reason={reason}
    >
      {t(END_REASON_LABEL[reason])}
    </Chip>
  );
}

function EndedAtCell({ row }: { readonly row: CreditGrantHistoryRow }): JSX.Element {
  const t = useT();
  const grant = row.grant;
  if (grant.status === 'active') {
    return <span>{t('credit.history.ends.ongoing')}</span>;
  }
  return (
    <div className="flex flex-col gap-1">
      {grant.ends_at !== null ? (
        <span className="text-caption text-paper-low" title={t('credit.history.ends.scheduled')}>
          {formatLocalTimestamp(grant.ends_at)}
        </span>
      ) : null}
      {grant.ended_at !== null ? (
        <span title={t('credit.history.ends.actual')}>{formatLocalTimestamp(grant.ended_at)}</span>
      ) : null}
      {grant.end_reason !== null ? (
        <EndReasonChip grantId={grant.id} reason={grant.end_reason} />
      ) : null}
    </div>
  );
}

function OpsEventList({
  grantId,
  events,
}: {
  readonly grantId: string;
  readonly events: readonly CreditOpsEvent[];
}): JSX.Element {
  const t = useT();
  if (events.length === 0) {
    return (
      <p className="m-0 text-dense text-paper-low" data-testid={`credit-grants-events-${grantId}`}>
        {t('credit.history.events.empty')}
      </p>
    );
  }
  return (
    <ul className="m-0 flex list-none flex-col gap-3 p-0" data-testid={`credit-grants-events-${grantId}`}>
      {events.map((event) => (
        <li key={event.id} className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-dense text-paper">{t(eventTypeLabelKey(event.event_type))}</span>
            <Chip tone={AUDIENCE_TONE[event.audience] ?? 'neutral'}>
              {t(audienceLabelKey(event.audience))}
            </Chip>
            <span className="font-mono text-micro text-paper-low">
              {formatLocalTimestamp(event.created_at, { seconds: true })}
            </span>
          </div>
          <pre className="m-0 max-h-80 overflow-auto whitespace-pre-wrap rounded-lg bg-well p-3 font-mono text-micro text-paper">
            {prettyPayload(event.payload_json)}
          </pre>
        </li>
      ))}
    </ul>
  );
}

function HistoryRowGroup({
  row,
  expanded,
  onToggle,
  cnyPerUsd,
}: {
  readonly row: CreditGrantHistoryRow;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const grant = row.grant;
  const label = row.org_name ?? row.org_id ?? grant.account_id;
  const salesperson =
    row.customer_manager === null ? t('credit.overview.dash') : row.customer_manager.display_name;
  const daysCell =
    row.days_to_topup === null
      ? t('credit.overview.dash')
      : t('credit.history.days.day', { n: String(row.days_to_topup) });

  return (
    <>
      <Tr interactive={false} data-testid={`credit-grants-row-${grant.id}`}>
        <Td>
          <div className="flex items-center gap-2">
            <IconButton
              size="sm"
              label={expanded ? t('credit.history.collapse') : t('credit.history.expand')}
              onClick={onToggle}
              aria-expanded={expanded}
              data-testid={`credit-grants-expand-${grant.id}`}
            >
              {expanded ? (
                <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
              )}
            </IconButton>
            <CustomerCell
              orgId={row.org_id}
              accountId={row.org_id === null ? grant.account_id : null}
              testId={`credit-grants-customer-${grant.id}`}
              secondary={
                row.org_id !== null ? (
                  <Link
                    className="self-start text-accent hover:underline"
                    to={`/crm/customers/${encodeURIComponent(row.org_id)}?tab=credit`}
                  >
                    {label}
                  </Link>
                ) : (
                  <span className="self-start font-mono text-micro">{label}</span>
                )
              }
            />
          </div>
        </Td>
        <Td>{salesperson}</Td>
        <Td>
          <Chip tone={GRANT_TYPE_TONE[grant.grant_type]}>{t(GRANT_TYPE_LABEL[grant.grant_type])}</Chip>
        </Td>
        <Td mono>{formatLocalTimestamp(grant.starts_at)}</Td>
        <Td>
          <EndedAtCell row={row} />
        </Td>
        <Td num>
          <GrantAmount grant={grant} cnyPerUsd={cnyPerUsd} />
        </Td>
        <Td num>
          <UsedAmount
            grantId={grant.id}
            usedUsdMicros={row.used_usd_micros}
            usedMeasuredAt={row.used_measured_at}
            cnyPerUsd={cnyPerUsd}
          />
        </Td>
        <Td num>
          <RepaymentCell
            grantId={grant.id}
            repaidUsdMicros={row.repaid_usd_micros}
            outstandingUsdMicros={row.outstanding_usd_micros}
            repaymentStatus={row.repayment_status}
            oneShotStatement={row.one_shot_statement}
            cnyPerUsd={cnyPerUsd}
          />
        </Td>
        <Td data-testid={`credit-grants-days-${grant.id}`}>{daysCell}</Td>
      </Tr>
      {expanded ? (
        <Tr interactive={false}>
          <Td colSpan={9}>
            <OpsEventList grantId={grant.id} events={row.ops_events} />
          </Td>
        </Tr>
      ) : null}
    </>
  );
}

export function CreditGrantHistoryPage(): JSX.Element {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  const [expandedGrantId, setExpandedGrantId] = useState<string | null>(null);

  const customer = (searchParams.get('customer') ?? '').trim();
  const manager = (searchParams.get('manager') ?? '').trim();
  const grantType = parseGrantTypeParam(searchParams.get('type'));
  const status = parseStatusParam(searchParams.get('status'));
  const endReason = parseEndReasonParam(searchParams.get('reason'));
  const startsFrom = (searchParams.get('from') ?? '').trim();
  const startsTo = (searchParams.get('to') ?? '').trim();
  const offset = parseOffsetParam(searchParams.get('offset'));
  const [customerDraft, setCustomerDraft] = useState(customer);
  const [managerDraft, setManagerDraft] = useState(manager);

  const queryParams = useMemo(
    () => ({
      ...(customer.length > 0 ? { customer } : {}),
      ...(manager.length > 0 ? { manager } : {}),
      ...(grantType === undefined ? {} : { grantType }),
      ...(status === 'all' ? {} : { status }),
      ...(endReason === undefined ? {} : { endReason }),
      ...(startsFrom.length > 0 ? { startsFrom } : {}),
      ...(startsTo.length > 0 ? { startsTo } : {}),
      limit: PAGE_LIMIT,
      offset,
    }),
    [customer, manager, grantType, status, endReason, startsFrom, startsTo, offset],
  );

  const query = useCreditGrantHistory(queryParams, true);
  const rows = query.data?.grants ?? [];
  const summary = query.data?.summary ?? null;
  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const page = Math.floor(offset / PAGE_LIMIT) + 1;

  function applyScope(mutate: (clone: URLSearchParams) => void, resetOffset = true): void {
    const clone = new URLSearchParams(searchParams);
    mutate(clone);
    if (resetOffset) clone.delete('offset');
    setSearchParams(clone, { replace: true });
    setExpandedGrantId(null);
  }

  function setTextFilter(key: string, value: string): void {
    applyScope((clone) => {
      const trimmed = value.trim();
      if (trimmed.length === 0) clone.delete(key);
      else clone.set(key, trimmed);
    });
  }

  function commitSearchTexts(nextCustomer: string, nextManager: string): void {
    const nextC = nextCustomer.trim();
    const nextM = nextManager.trim();
    setCustomerDraft(nextC);
    setManagerDraft(nextM);
    if (nextC === customer && nextM === manager) return;
    applyScope((clone) => {
      if (nextC.length === 0) clone.delete('customer');
      else clone.set('customer', nextC);
      if (nextM.length === 0) clone.delete('manager');
      else clone.set('manager', nextM);
    });
  }

  const grantTypeOptions = [
    { value: FILTER_ALL, label: t('credit.history.filter.all') },
    ...CREDIT_GRANT_TYPES.map((value) => ({ value, label: t(GRANT_TYPE_LABEL[value]) })),
  ];
  const statusOptions = CREDIT_GRANT_HISTORY_STATUS_FILTERS.map((value) => ({
    value,
    label: t(STATUS_LABEL[value]),
  }));
  const reasonOptions = [
    { value: FILTER_ALL, label: t('credit.history.filter.all') },
    ...CREDIT_GRANT_END_REASONS.map((value) => ({
      value,
      label: t(END_REASON_LABEL[value]),
    })),
  ];

  const avgDays =
    summary === null || summary.average_days_to_topup === null
      ? t('credit.overview.dash')
      : String(summary.average_days_to_topup);

  return (
    <div className="flex flex-col gap-6" data-testid="credit-grants-page">
      <PageHeader title={t('credit.history.title')} sub={t('credit.history.subtitle')} />

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border border-line-soft bg-card p-4"
        data-testid="credit-grants-filters"
        onSubmit={(event) => {
          event.preventDefault();
          commitSearchTexts(customerDraft, managerDraft);
        }}
      >
        <FormField label={t('credit.history.filter.customer')} fieldId="credit-grants-filter-org">
          <Input
            id="credit-grants-filter-org"
            value={customerDraft}
            onChange={(event) => setCustomerDraft(event.currentTarget.value)}
            onBlur={(event) => commitSearchTexts(event.currentTarget.value, managerDraft)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              commitSearchTexts(event.currentTarget.value, managerDraft);
            }}
            placeholder={t('credit.history.filter.customer_placeholder')}
            maxLength={CREDIT_GRANT_HISTORY_QUERY_MAX_CHARS}
            className="w-56"
            data-testid="credit-grants-filter-org"
          />
        </FormField>
        <FormField
          label={t('credit.history.filter.salesperson')}
          fieldId="credit-grants-filter-manager"
        >
          <Input
            id="credit-grants-filter-manager"
            value={managerDraft}
            onChange={(event) => setManagerDraft(event.currentTarget.value)}
            onBlur={(event) => commitSearchTexts(customerDraft, event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              commitSearchTexts(customerDraft, event.currentTarget.value);
            }}
            placeholder={t('credit.history.filter.salesperson_placeholder')}
            maxLength={CREDIT_GRANT_HISTORY_QUERY_MAX_CHARS}
            className="w-56"
            data-testid="credit-grants-filter-manager"
          />
        </FormField>
        <FormField label={t('credit.history.filter.grant_type')} fieldId="credit-grants-filter-type">
          <Select
            value={grantType ?? FILTER_ALL}
            onValueChange={(value) => {
              if (value === FILTER_ALL || isCreditGrantType(value)) {
                setTextFilter('type', value === FILTER_ALL ? '' : value);
              }
            }}
            options={grantTypeOptions}
            ariaLabel={t('credit.history.filter.grant_type')}
            testId="credit-grants-filter-type"
            className="w-44"
          />
        </FormField>
        <FormField label={t('credit.history.filter.status')} fieldId="credit-grants-filter-status">
          <Select
            value={status}
            onValueChange={(value) => {
              if (isCreditGrantHistoryStatusFilter(value)) {
                setTextFilter('status', value === 'all' ? '' : value);
              }
            }}
            options={statusOptions}
            ariaLabel={t('credit.history.filter.status')}
            testId="credit-grants-filter-status"
            className="w-36"
          />
        </FormField>
        <FormField
          label={t('credit.history.filter.end_reason')}
          fieldId="credit-grants-filter-reason"
        >
          <Select
            value={endReason ?? FILTER_ALL}
            onValueChange={(value) => {
              if (value === FILTER_ALL || isCreditGrantEndReason(value)) {
                setTextFilter('reason', value === FILTER_ALL ? '' : value);
              }
            }}
            options={reasonOptions}
            ariaLabel={t('credit.history.filter.end_reason')}
            testId="credit-grants-filter-reason"
            className="w-40"
          />
        </FormField>
        <FormField
          label={t('credit.history.filter.starts_from')}
          fieldId="credit-grants-filter-from"
        >
          <Input
            id="credit-grants-filter-from"
            type="date"
            value={startsFrom}
            onChange={(event) => setTextFilter('from', event.currentTarget.value)}
            data-testid="credit-grants-filter-from"
          />
        </FormField>
        <FormField label={t('credit.history.filter.starts_to')} fieldId="credit-grants-filter-to">
          <Input
            id="credit-grants-filter-to"
            type="date"
            value={startsTo}
            onChange={(event) => setTextFilter('to', event.currentTarget.value)}
            data-testid="credit-grants-filter-to"
          />
        </FormField>
      </form>

      {summary !== null ? (
        <div className="flex flex-col gap-2">
          <p className="m-0 text-label text-paper-low">{t('credit.history.summary.scope')}</p>
          <div className="grid gap-3 sm:grid-cols-4">
            <KpiCard
              label={t('credit.history.kpi.total')}
              value={String(summary.total)}
              testId="credit-grants-summary-total"
            />
            <KpiCard
              label={t('credit.history.kpi.active')}
              value={String(summary.active_count)}
              testId="credit-grants-summary-active"
            />
            <KpiCard
              label={t('credit.history.kpi.ended')}
              value={String(summary.ended_count)}
              testId="credit-grants-summary-ended"
            />
            <KpiCard
              label={t('credit.history.kpi.avg_days')}
              value={avgDays}
              testId="credit-grants-summary-avg-days"
            />
          </div>
        </div>
      ) : null}

      <Panel caption={t('credit.history.table.caption')} testId="credit-grants-panel">
        {query.isPending ? <TableSkeleton rows={4} testId="credit-grants-loading" /> : null}
        {query.isError ? (
          <EmptyState
            title={t('credit.history.load_error')}
            hint={coerceErrorMessage(query.error)}
            action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
            testId="credit-grants-error"
          />
        ) : null}
        {query.data !== undefined ? (
          <>
            <Table testId="credit-grants-table" className="rounded-none border-0">
              <THead>
                <Tr interactive={false}>
                  <Th>{t('credit.history.col.customer')}</Th>
                  <Th>{t('credit.history.col.salesperson')}</Th>
                  <Th>{t('credit.history.col.grant_type')}</Th>
                  <Th>{t('credit.history.col.starts_at')}</Th>
                  <Th>{t('credit.history.col.ends_at')}</Th>
                  <Th num>{t('credit.history.col.amount')}</Th>
                  <Th num title={t('credit.history.used.tooltip')}>
                    {t('credit.history.col.used')}
                  </Th>
                  <Th num title={t('credit.history.repayment.tooltip')}>
                    {t('credit.history.col.repayment')}
                  </Th>
                  <Th>{t('credit.history.col.days_to_topup')}</Th>
                </Tr>
              </THead>
              <TBody>
                {rows.length === 0 ? (
                  <TableEmpty colSpan={9} testId="credit-grants-empty">
                    {t('credit.history.empty')}
                  </TableEmpty>
                ) : (
                  rows.map((row) => (
                    <HistoryRowGroup
                      key={row.grant.id}
                      row={row}
                      expanded={expandedGrantId === row.grant.id}
                      cnyPerUsd={cnyPerUsd}
                      onToggle={() =>
                        setExpandedGrantId((current) =>
                          current === row.grant.id ? null : row.grant.id,
                        )
                      }
                    />
                  ))
                )}
              </TBody>
            </Table>
            {total > 0 ? (
              <div className="border-t border-line-soft px-4 py-3">
                <Pagination
                  testId="credit-grants-pagination"
                  page={page}
                  pageCount={pageCount}
                  onPageChange={(nextPage) =>
                    applyScope((clone) => {
                      const nextOffset = (nextPage - 1) * PAGE_LIMIT;
                      if (nextOffset > 0) clone.set('offset', String(nextOffset));
                      else clone.delete('offset');
                    }, false)
                  }
                  prevLabel={t('common.previous')}
                  nextLabel={t('common.next')}
                  ariaLabel={t('credit.history.pagination.aria_label')}
                  full={{
                    summary: t('credit.history.pagination.range', {
                      start: total === 0 ? 0 : offset + 1,
                      end: Math.min(offset + rows.length, total),
                      total,
                    }),
                    firstLabel: t('common.pagination.first'),
                    lastLabel: t('common.pagination.last'),
                    skipPrevLabel: t('common.pagination.skip_prev'),
                    skipNextLabel: t('common.pagination.skip_next'),
                    jumpAriaLabel: t('common.pagination.jump'),
                    jumpSubmitLabel: t('common.pagination.jump_submit'),
                    pageLabel: (n) => t('common.pagination.page', { page: String(n) }),
                    busy: query.isFetching,
                  }}
                />
              </div>
            ) : null}
          </>
        ) : null}
      </Panel>
    </div>
  );
}
